const terminalStatuses = new Set(['completed', 'failed', 'skipped']);
export const isFileProcessing = file => !file?.isUploading && ['pending', 'processing'].includes(file?.processingStatus?.status);

// One poller per file, shared by every composer/source picker. Bound wall time,
// including authentication, response bodies and network retries, not just the
// number of successful requests. A late response must never undo a socket event.
export function createFileProcessingPoller({ fetchFile, getFile, updateFile }) {
  const active = new Map();
  return (fileId, { onStatusChange, maxAttempts = 60, intervalMs = 2000, timeoutMs = 120000 } = {}) => {
    const key = String(fileId);
    if (active.has(key)) {
      const existing = active.get(key);
      if (onStatusChange) existing.listeners.add(onStatusChange);
      return existing.promise;
    }
    const entry = { listeners: new Set(onStatusChange ? [onStatusChange] : []) };
    let finished = false;
    let previousStatus;
    const deadline = Date.now() + timeoutMs;
    const terminalFile = () => {
      const file = getFile(key);
      const status = file?.processingStatus?.status;
      return terminalStatuses.has(status) && (status !== 'failed' || !file.processingStatus.statusCheckTimedOut) ? file : null;
    };
    const publish = file => {
      updateFile(key, file);
      if (file.processingStatus?.status !== previousStatus) {
        previousStatus = file.processingStatus?.status;
        entry.listeners.forEach(listener => {
          try { listener(file.processingStatus); } catch (_) { /* observer only */ }
        });
      }
      return file;
    };
    const run = async () => {
      // A manual recheck gets a fresh budget without re-uploading the file.
      const original = getFile(key);
      if (original?.processingStatus?.status === 'failed' && original.processingStatus.statusCheckTimedOut) {
        publish({ ...original, processingStatus: { status: 'processing' } });
      }
      for (let attempt = 0; attempt < maxAttempts && Date.now() < deadline; attempt++) {
        const known = terminalFile();
        if (known) return publish(known);
        let timer;
        try {
          const remaining = deadline - Date.now();
          const file = await Promise.race([
            fetchFile(key, {
              timeoutMs: Math.min(10000, remaining),
              networkRetries: 0,
              isRequestCurrent: () => !finished && Date.now() < deadline,
            }),
            new Promise((_, reject) => {
              timer = setTimeout(() => reject(new Error('Status check timed out')), remaining);
            }),
          ]);
          const completedDuringRequest = terminalFile();
          if (completedDuringRequest) return publish(completedDuringRequest);
          if (!file || String(file.id || file._id) !== key || !file.processingStatus?.status) {
            throw new Error('Missing file status');
          }
          publish(file);
          if (terminalStatuses.has(file.processingStatus.status)) return file;
        } catch (_) {
          // A lost status response is not evidence that extraction failed.
        } finally {
          clearTimeout(timer);
        }
        if (attempt + 1 < maxAttempts && Date.now() < deadline) {
          await new Promise(resolve => setTimeout(resolve, Math.min(intervalMs, deadline - Date.now())));
        }
      }
      const known = terminalFile();
      if (known) return publish(known);
      return publish({
        ...getFile(key),
        id: key,
        processingStatus: {
          status: 'failed',
          statusCheckTimedOut: true,
          error: 'Unable to confirm file readiness. Check status again or remove and upload the file again.',
        },
      });
    };
    entry.promise = Promise.resolve().then(run).finally(() => {
      finished = true;
      active.delete(key);
    });
    active.set(key, entry);
    return entry.promise;
  };
}
